<template>
  <button
    type="button"
    class="w-full text-left cursor-pointer focus:outline-none"
    data-test="other-sellers-offers-section"
    @click="$emit('select-offer')"
  >
    <!-- Product Header -->
    <div class="flex items-start gap-16">
      <picture class="shrink-0">
        <RevIllustrationSource
          v-if="productImage"
          media="(min-width: 768px)"
          :alt="productImageAlt"
          class="object-contain aspect-square"
          data-test="product-thumbnail-desktop"
          :height="132"
          :src="productImage"
          :width="132"
        /><RevIllustrationSource
          v-if="productImage"
          media="(max-width: 767px)"
          :alt="productImageAlt"
          class="rounded-md object-contain aspect-square"
          data-test="product-thumbnail-mobile"
          :height="64"
          :src="productImage"
          :width="64"
        />
        <RevIllustration
          v-if="productImage"
          :alt="productImageAlt"
          :height="132"
          :src="productImage"
          :width="132"
        />
      </picture>

      <div class="flex flex-col gap-8">
        <FormattedMessage
          tag="h2"
          class="heading-1"
          :definition="translations.allOffersFor"
        >
          <template #productName>
            <span class="text-action-brand-hi">{{ productName }}</span>
          </template>
        </FormattedMessage>

        <p class="mt-4 text-caption text-static-default-low">
          {{ i18n(translations.selectedOptionsIntro) }}
        </p>

        <div
          v-if="selectedTags.length"
          class="flex flex-wrap gap-8 -ms-[72px] md:-ms-0"
        >
          <RevTag
            v-for="tag in selectedTags"
            :key="tag"
            :label="tag"
            variant="secondary"
            variation="outline"
          />
        </div>
      </div>
    </div>

    <!-- Offers List -->
    <div class="relative mt-24">
      <ul class="flex flex-col gap-8" :hide-dividers="true">
        <li
          v-for="offer in offers"
          :key="offer.id"
          class="rounded-lg bg-surface-default-low p-16 shadow-[0_2px_4px_0_rgba(15,17,23,0.05)]"
          data-test="offer-item"
        >
          <div
            class="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 w-full"
          >
            <!-- Price & Delivery Pill -->
            <div
              class="flex flex-wrap items-center gap-12 justify-between md:justify-normal w-full md:w-auto"
            >
              <span class="relative inline-block">
                <span class="heading-2">{{ offer.price }}</span>

                <span
                  aria-hidden="true"
                  class="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,#FFFFFF_80%)] backdrop-blur-[1.8px] [mask:linear-gradient(90deg,transparent_0%,#000_30%,#000)]"
                  data-test="price-gradient-overlay"
                />
              </span>

              <span
                class="inline-flex items-center gap-4 rounded-sm bg-static-info-low p-4 text-body-2 text-action-default-hi"
              >
                <IconTruck class="h-16 w-16" />
                {{
                  i18n(translations.deliveryInDaysRange, {
                    minDays: offer.minDays,
                    maxDays: offer.maxDays,
                  })
                }}
              </span>
            </div>

            <!-- Refurbished by & Rating -->
            <div
              class="flex flex-wrap items-center gap-8 md:justify-start lg:justify-end md:basis-1/2 lg:basis-1/3"
            >
              <span class="body-2 text-static-default-low">
                <FormattedMessage :definition="translations.refurbishedBy">
                  <template #sellerName>
                    <span class="underline text-static-default-hi">{{
                      offer.sellerName
                    }}</span>
                  </template>
                </FormattedMessage>
              </span>

              <span
                class="inline-flex items-center gap-4 rounded-full bg-[#F0EBE5] px-6 py-2 text-caption text-static-default-hi"
              >
                <IconStarFilled class="h-12 w-12 text-static-default-low" />
                {{ i18n(translations.rating, { rating: offer.rating }) }}
              </span>
            </div>

            <!-- Mimicked CTA -->
            <div
              class="shrink-0 w-full md:w-auto md:self-auto md:basis-1/6 mt-8 md:mt-0 md:flex md:justify-end"
            >
              <span
                v-if="offer.isSelected"
                :class="CTA_CLASS"
                class="border-static-default-mid bg-surface-default-mid body-2 text-static-default-low"
                data-test="offer-currently-selected"
              >
                {{ i18n(translations.currentlySelected) }}
              </span>

              <span
                v-else
                :class="CTA_CLASS"
                class="border-static-default-hi bg-surface-default-low body-2-bold text-static-default-hi"
                data-test="offer-select-cta"
              >
                {{ i18n(translations.selectOffer) }}
              </span>
            </div>
          </div>
        </li>
      </ul>

      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-x-0 bottom-0 h-[120px] md:h-[109px] bg-[linear-gradient(180deg,rgba(245,243,240,0)_0%,#F5F3F0_80%)] backdrop-blur-[1.8px] [mask:linear-gradient(180deg,transparent_0%,#000_30%,#000)]"
        data-test="offers-gradient-overlay"
      />
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'
import { RevIllustrationSource } from '@ds/components/IllustrationSource'
import { RevTag } from '@ds/components/Tag'
import { IconStarFilled } from '@ds/icons/IconStarFilled'
import { IconTruck } from '@ds/icons/IconTruck'
import { tw } from '@ds/utils/tailwind'

import translations from '../../OtherSellersDrawer.translations'

import {
  FAKE_OFFERS,
  type FakeOffer,
} from './OtherSellersOffersSection.constants'

const props = withDefaults(
  defineProps<{
    productName: string
    productImage?: string | null
    productImageAlt?: string
    selectedTags?: string[]
    selectedPrice?: Price | null
    selectedSellerName?: string
    selectedSellerRating?: number | null
  }>(),
  {
    productImage: null,
    productImageAlt: '',
    selectedTags: () => [],
    selectedPrice: null,
    selectedSellerName: undefined,
    selectedSellerRating: null,
  },
)

defineEmits<{
  (e: 'select-offer'): void
}>()

const i18n = useI18n()

const offsetOrder = ref([5, 10, 15])

onMounted(() => {
  offsetOrder.value = [...offsetOrder.value].sort(() => Math.random() - 0.5)
})

const offers = computed<FakeOffer[]>(() => {
  const { selectedPrice, selectedSellerName, selectedSellerRating } = props

  if (!selectedPrice) return FAKE_OFFERS

  const baseAmount = parseFloat(selectedPrice.amount)
  let offsetIndex = 0

  return FAKE_OFFERS.map((offer) => {
    if (offer.isSelected) {
      return {
        ...offer,
        price: i18n.price(selectedPrice),
        sellerName: selectedSellerName ?? offer.sellerName,
        rating:
          selectedSellerRating !== null
            ? i18n.number(selectedSellerRating, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : offer.rating,
      }
    }

    const offset = offsetOrder.value[offsetIndex] ?? 0
    offsetIndex += 1

    return {
      ...offer,
      price: i18n.price({
        amount: (baseAmount + offset).toFixed(2),
        currency: selectedPrice.currency,
      }),
    }
  })
})

const CTA_CLASS = tw`flex items-center justify-center w-full md:w-auto md:shrink-[1] rounded-md border px-12 py-6 whitespace-pre`
</script>
